/**
 * Local Storage Utility
 * Simple helper functions for managing local storage with type safety
 */

// Define keys for all local storage items
export const LOCAL_STORAGE_KEYS = {
    MEALPLAN_DRAFT: 'mealplan-draft',
    MEALPLAN_TEMPLATES: 'mealplan-templates',
    API_CONFIG: 'api-config',
    TOKEN_USAGE: 'token-usage',
    SEARCH_HISTORY: 'search-history',
    SEARCH_MODE: 'search-mode',
    REFILL_SETTINGS: 'refill-settings',
    OPENAI_API_KEY: 'openai-api-key',
    OPENAI_PROMPT: 'openai-prompt',
    OPENAI_MODEL: 'openai-model',
    OPENAI_TEMPERATURE: 'openai-temperature',
    OPENAI_MAX_TOKENS: 'openai-max-tokens',
    OPENAI_TOP_P: 'openai-top-p',
    OPENAI_FREQUENCY_PENALTY: 'openai-frequency-penalty',
    OPENAI_PRESENCE_PENALTY: 'openai-presence-penalty',
    PASSIO_PROMPT: 'passio-prompt',
    PASSIO_MODEL: 'passio-model',
    PASSIO_TEMPERATURE: 'passio-temperature',
    PASSIO_MAX_TOKENS: 'passio-max-tokens',
    PASSIO_TOP_P: 'passio-top-p',
    PASSIO_FREQUENCY_PENALTY: 'passio-frequency-penalty',
    PASSIO_PRESENCE_PENALTY: 'passio-presence-penalty',
} as const;

// Type for search mode
export type SearchMode = "selective" | "smart";

/**
 * Get item from local storage with type safety
 */
export function getLocalStorageItem<T>(key: string, defaultValue: T): T {
    try {
        const item = localStorage.getItem(key);
        if (item === null) {
            return defaultValue;
        }
        return JSON.parse(item) as T;
    } catch (error) {
        console.error(`Error reading from localStorage key "${key}":`, error);
        return defaultValue;
    }
}

/**
 * Set item in local storage
 */
export function setLocalStorageItem<T>(key: string, value: T): void {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error(`Error writing to localStorage key "${key}":`, error);
    }
}

/**
 * Remove item from local storage
 */
export function removeLocalStorageItem(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error(`Error removing localStorage key "${key}":`, error);
    }
}

/**
 * Get search mode preference
 */
export function getSearchModePreference(): SearchMode {
    return getLocalStorageItem<SearchMode>(
        LOCAL_STORAGE_KEYS.SEARCH_MODE,
        "selective"
    );
}

/**
 * Set search mode preference
 */
export function setSearchModePreference(mode: SearchMode): void {
    setLocalStorageItem(LOCAL_STORAGE_KEYS.SEARCH_MODE, mode);
}

/**
 * Clear all app-specific local storage items
 */
export function clearAllLocalStorage(): void {
    Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
        removeLocalStorageItem(key);
    });
}
